import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, ShieldCheck, Landmark, Lock, BadgeCheck, FileCheck2, UserPlus, Phone, Sparkles, Heart, ChevronLeft, ChevronRight } from "lucide-react";
import api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import SEO from "../../components/SEO";
import { SectionLabel, LinkBtn, LazyImg, WorkIcon, ShareButtons, EmptyState, HtmlBlock } from "../../components/Bits";
import { pickLocalized } from "../../lib/localized";

const TESTIMONIALS = [
  { text: "The Foundation guided my daughter's school admission and arranged study material. The team stayed in touch until everything was completed.", name: "Sunita Devi", role: "Parent" },
  { text: "I received clear help with my disability documents and social security application. The volunteers were respectful at every step.", name: "Ramesh Kumar", role: "Programme participant" },
  { text: "Volunteering with Sahaj Sarathi Foundation showed me how organised community work can change lives in our own neighbourhood.", name: "Anjali Mishra", role: "Volunteer" },
];

export default function Home() {
  const { t, L, site, lang } = useApp();
  const [greeting, setGreeting] = useState(null);
  const [articles, setArticles] = useState([]);
  const [projects, setProjects] = useState([]);
  const [news, setNews] = useState([]);
  const [gallery, setGallery] = useState([]);
  const hero = site?.hero || {};
  const settings = site?.settings || {};
  const areas = site?.work_areas || [];

  useEffect(() => {
    api.get("/public/greetings/today").then((r) => setGreeting(r.data?.id ? r.data : null)).catch(() => {});
    api.get("/public/articles?limit=3&home=true").then((r) => setArticles(r.data.items)).catch(() => {});
    api.get("/public/projects?limit=3&home=true").then((r) => setProjects(r.data.items)).catch(() => {});
    api.get("/public/news?limit=3&home=true").then((r) => setNews(r.data.items)).catch(() => {});
    api.get("/public/gallery?home=true").then((r) => setGallery(r.data)).catch(() => {});
  }, []);

  const [slide, setSlide] = useState(0);
  const totalHeroSlides = Math.max(3, (site?.hero_slides || []).length);
  useEffect(() => {
    const id = setInterval(() => setSlide((s) => (s + 1) % totalHeroSlides), 6500);
    return () => clearInterval(id);
  }, [totalHeroSlides]);

  const badges = [
    [BadgeCheck, t("registered_ngo")], [Landmark, t("ngo_darpan")], [FileCheck2, t("udyam")],
    [ShieldCheck, t("govt_recog")], [Lock, t("secure_donations")],
  ];
  const steps = [
    { icon: Phone, title_en: "Register or Contact Us", title_hi: "पंजीकरण करें या संपर्क करें", desc_en: "Reach the Foundation by phone, email or the online registration form and share your requirement.", desc_hi: "फोन, ईमेल या ऑनलाइन पंजीकरण फॉर्म से फाउंडेशन से संपर्क करें।" },
    { icon: FileCheck2, title_en: "Verification and Assessment", title_hi: "सत्यापन और मूल्यांकन", desc_en: "Our team verifies the details and assesses the kind of support that will help the most.", desc_hi: "हमारी टीम विवरण सत्यापित करती है और उपयुक्त सहायता का मूल्यांकन करती है।" },
    { icon: UserPlus, title_en: "Support and Follow-up", title_hi: "सहायता और अनुवर्ती", desc_en: "Assistance is provided and our volunteers stay in touch to follow up on progress.", desc_hi: "सहायता प्रदान की जाती है और स्वयंसेवक प्रगति पर संपर्क में रहते हैं।" },
  ];

  return (
    <div data-testid="home-page">
      <SEO title={null} description={settings.tagline_en} image={hero.image} />

      {/* HERO SLIDER (ATS-style full-screen cinematic carousel) */}
      {(() => {
        const cmsSlides = (site?.hero_slides || []).map((sl) => ({
          img: sl.image || "/api/uploads/hero_cinematic.jpg",
          head: (lang === "hi" && sl.heading_hi ? sl.heading_hi : sl.heading_en) || "Education. Health. Social Security. Dignity for All.",
          sub: (lang === "hi" && sl.sub_hi ? sl.sub_hi : sl.sub_en) || settings.tagline_en,
          primary: [(lang === "hi" && sl.cta_text_hi ? sl.cta_text_hi : sl.cta_text_en) || t("donate"), sl.cta_link || "/donate"],
          secondary: [t("explore_work"), "/work"],
          full: !!sl.full, hotspots: sl.hotspots !== false, alt: sl.alt_text || "",
        }));
        const baseSlides = [
          {
            img: hero.image || "/api/uploads/hero_cinematic.jpg",
            head: lang === "hi" && hero.heading_hi ? hero.heading_hi : (hero.heading_en || "Education. Health. Social Security. Dignity for All."),
            sub: lang === "hi" && hero.sub_hi ? hero.sub_hi : (hero.sub_en || settings.tagline_en),
            primary: [t("donate"), "/donate"], secondary: [t("explore_work"), "/work"],
          },
          {
            img: "/api/uploads/hero_main.jpg",
            head: lang === "hi" ? "समान अधिकार। समान भागीदारी।" : "Equal Rights. Equal Participation.",
            sub: lang === "hi" ? "शिक्षा और स्वास्थ्य से लेकर सामाजिक सुरक्षा तक — हम हर कदम पर वंचित समुदायों के साथ खड़े हैं।" : "From education and health to social security — we stand with vulnerable communities at every step.",
            primary: [t("explore_work"), "/work"], secondary: [t("join_volunteer"), "/register"],
          },
          {
            img: "/api/uploads/hero_slide3.jpg",
            head: lang === "hi" ? "हर व्यक्ति गरिमा का अधिकारी है।" : "Every Person Deserves Dignity.",
            sub: lang === "hi" ? "स्वयंसेवक बनें या किसी कार्यक्रम का समर्थन करें — आपका समय और सहयोग जीवन बदलते हैं।" : "Join us as a volunteer or support a programme — your time and kindness change lives.",
            primary: [t("join_volunteer"), "/register"], secondary: [t("donate"), "/donate"],
          },
        ];
        const slides = cmsSlides.length ? [...cmsSlides, ...baseSlides.slice(cmsSlides.length)] : baseSlides;
        const s = slides[slide];
        const parts = s.head.split(".").map((x) => x.trim()).filter(Boolean);
        const lastSentence = parts.pop();
        return (
          <section className={`relative ${s.full ? "" : "min-h-[92vh]"} overflow-hidden bg-navy`} data-testid="hero-slider">
            {slides.map((sl, i) => (
              <div key={i} className={`${sl.full && i === 0 ? "relative w-full" : "absolute inset-0"} transition-opacity duration-1000 ${i === slide ? "opacity-100" : "pointer-events-none opacity-0"}`} aria-hidden={i !== slide} style={sl.full && i === 0 ? { aspectRatio: "1536 / 644" } : undefined}>
                <img src={sl.img} alt={sl.alt || (i === 0 ? "Sahaj Sarathi Foundation – Education, Health, Social Security and Equal Participation" : "")} className={sl.full ? "h-full w-full object-cover" : `h-full w-full object-cover max-sm:object-contain max-sm:object-top ${i === slide ? "animate-kenburns max-sm:animate-none" : ""}`} loading={i === 0 ? "eager" : "lazy"} fetchPriority={i === 0 ? "high" : undefined} />
                {!sl.full && (<>
                <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/60 to-navy/15" aria-hidden="true" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-navy/10" aria-hidden="true" />
                </>)}
                {sl.full && sl.hotspots && i === slide && (<>
                  <Link to="/donate" aria-label="Donate Now" data-testid="hero-cta-primary" className="absolute rounded-full outline-none focus-visible:ring-4 focus-visible:ring-saffron-400" style={{ left: "5.5%", top: "67.7%", width: "13.5%", height: "11.5%" }} />
                  <Link to="/work" aria-label="Explore Our Work" data-testid="hero-cta-secondary" className="absolute rounded-full outline-none focus-visible:ring-4 focus-visible:ring-saffron-400" style={{ left: "19.3%", top: "67.7%", width: "14%", height: "11.5%" }} />
                </>)}
              </div>
            ))}
            {!s.full && (
            <div className="absolute inset-0 flex items-center">
            <div className="container-ssf relative w-full pb-44 pt-28">
              <div key={slide} className="max-w-2xl animate-fade-up">
                <h1 data-testid="hero-heading" className="mt-6 font-display text-4xl font-black leading-[1.06] tracking-tight sm:text-5xl lg:text-[3.6rem]">
                  {parts.length > 0 && <span className="text-white">{parts.length > 1 ? `${parts.join(". ")}. ` : `${parts[0]} `}</span>}
                  <span className={parts.length > 0 ? "bg-gradient-to-r from-saffron-500 via-gold to-peach bg-clip-text text-transparent" : "text-white"}>{lastSentence}{/[\p{L}\p{N})]$/u.test(lastSentence) ? "." : ""}</span>
                </h1>
                <p data-testid="hero-sub" className="mt-6 max-w-xl text-base leading-relaxed text-slate-200 sm:text-lg">{s.sub}</p>
                <div className="mt-9 flex flex-wrap gap-3">
                  <Link to={s.primary[1]} className="btn-primary" data-testid="hero-cta-primary">{s.primary[0]} <ArrowRight size={16} /></Link>
                  <Link to={s.secondary[1]} className="btn-outline-light" data-testid="hero-cta-secondary">{s.secondary[0]}</Link>
                </div>
              </div>
            </div>
            </div>
            )}
            <div className="absolute bottom-24 right-4 z-10 flex items-center gap-2 sm:right-10">
              <button onClick={() => setSlide((slide + slides.length - 1) % slides.length)} aria-label="Previous slide" data-testid="hero-prev"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/25 bg-white/10 text-white backdrop-blur transition-colors hover:bg-saffron-500 hover:text-navy">
                <ChevronLeft size={18} />
              </button>
              {slides.map((_, i) => (
                <button key={i} onClick={() => setSlide(i)} aria-label={`Slide ${i + 1}`} data-testid={`hero-dot-${i}`}
                  className={`h-2.5 rounded-full transition-[width,background-color] duration-300 ${i === slide ? "w-7 bg-saffron-500" : "w-2.5 bg-white/40 hover:bg-white/70"}`} />
              ))}
              <button onClick={() => setSlide((slide + 1) % slides.length)} aria-label="Next slide" data-testid="hero-next"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/25 bg-white/10 text-white backdrop-blur transition-colors hover:bg-saffron-500 hover:text-navy">
                <ChevronRight size={18} />
              </button>
            </div>
            {!s.full && (
            <div className="absolute inset-x-0 bottom-0 border-t border-white/10 bg-navy/45 backdrop-blur-md">
              <div className="container-ssf grid grid-cols-2 gap-4 py-4 sm:grid-cols-3 lg:grid-cols-5">
                {badges.map(([Icon, label], i) => (
                  <div key={i} className="flex items-center gap-2.5 text-xs font-bold text-white/90 sm:text-sm" data-testid={`hero-badge-${i}`}>
                    <Icon size={19} className="shrink-0 text-saffron-400" strokeWidth={1.6} /> {label}
                  </div>
                ))}
              </div>
            </div>
            )}
          </section>
        );
      })()}

      {/* QUICK ACTION CARDS (ATS-style icon row) */}
      <section className="bg-ivory py-14">
        <div className="container-ssf grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            [Heart, t("donate"), "/donate", "donate"],
            [UserPlus, t("join_volunteer"), "/register", "volunteer"],
            [FileCheck2, t("participant_reg"), "/register", "register"],
            [Award, t("verify_cert"), "/verify", "verify"],
          ].map(([Icon, label, to, key]) => (
            <Link key={key} to={to} data-testid={`quick-card-${key}`}
              className="card-ssf group flex items-center gap-4 p-6 transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-xl">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-saffron-100 text-saffron-600 transition-colors duration-200 group-hover:bg-saffron-500 group-hover:text-navy">
                <Icon size={22} strokeWidth={1.7} />
              </span>
              <span>
                <span className="block font-display text-base font-bold text-navy">{label}</span>
                <span className="mt-0.5 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-saffron-600">
                  {lang === "hi" ? "और जानें" : "Learn More"} <ArrowRight size={12} />
                </span>
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 sm:py-24">
        <div className="container-ssf">
          <SectionLabel>{t("how_it_works")}</SectionLabel>
          <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
            {lang === "hi" ? "सरल और गरिमापूर्ण प्रक्रिया" : "A simple, dignified process"}
          </h2>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {steps.map((s, i) => (
              <div key={i} className="card-ssf group p-8 transition-[transform,box-shadow] duration-200 hover:-translate-y-1" data-testid={`step-${i}`}>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-saffron-100 text-saffron-600">
                  <s.icon size={22} strokeWidth={1.6} />
                </div>
                <p className="mt-5 font-display text-5xl font-bold text-saffron-500/25">{i + 1}</p>
                <h3 className="mt-2 text-lg font-bold text-navy">{lang === "hi" ? s.title_hi : s.title_en}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{lang === "hi" ? s.desc_hi : s.desc_en}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WORK AREAS BENTO */}
      <section className="bg-white py-20 sm:py-24">
        <div className="container-ssf">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <SectionLabel>{t("our_work")}</SectionLabel>
              <h2 className="mt-3 max-w-xl font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
                {lang === "hi" ? "हम जिन क्षेत्रों में कार्य करते हैं" : "Count on our community programmes"}
              </h2>
            </div>
            <Link to="/work" className="inline-flex items-center gap-1.5 text-sm font-bold text-saffron-600 hover:text-navy" data-testid="work-view-all">
              {t("view_all")} <ArrowRight size={15} />
            </Link>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {areas.slice(0, 8).map((a, i) => (
              <Link to="/work" key={a.id} data-testid={`work-card-${a.slug}`}
                className={`card-ssf group flex flex-col p-7 transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-xl ${i === 0 || i === 5 ? "sm:col-span-2" : ""}`}>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brandgreen-100 text-brandgreen">
                  <WorkIcon name={a.icon} className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display text-lg font-bold text-navy group-hover:text-saffron-600">{L(a, "title")}</h3>
                <HtmlBlock html={L(a, "short_desc") || L(a, "desc")} className="mt-2 text-sm leading-relaxed text-slate-600" testid={`home-work-desc-${a.slug}`} />
                {a.stats && <p className="mt-3 text-xs font-bold uppercase tracking-wider text-brandgreen" data-testid={`home-work-stats-${a.slug}`}>{a.stats}</p>}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      {projects.length > 0 && (
        <section className="bg-cream py-20" data-testid="home-projects-section">
          <div className="container-ssf">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <SectionLabel>{t("projects")}</SectionLabel>
                <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">{lang === "hi" ? "चुनिंदा परियोजनाएँ" : "Featured Projects"}</h2>
              </div>
              <Link to="/projects" className="inline-flex items-center gap-1.5 text-sm font-bold text-saffron-600 hover:text-navy" data-testid="projects-view-all">
                {t("view_all")} <ArrowRight size={15} />
              </Link>
            </div>
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {projects.map((p) => (
                <Link key={p.id} to={`/projects/${p.slug}`} className="card-ssf group flex flex-col overflow-hidden transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-xl" data-testid={`home-project-${p.slug}`}>
                  {p.image && <LazyImg src={p.image} alt={p.alt_text || p.title_en} className="aspect-[16/9] w-full object-cover" />}
                  <div className="flex flex-1 flex-col p-6">
                    <span className="text-xs font-bold uppercase tracking-wider text-saffron-600">{p.category}</span>
                    <h3 className="mt-2 font-display text-xl font-bold text-navy group-hover:text-saffron-600">{L(p, "title")}</h3>
                    <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-slate-600">{L(p, "short_desc") || String(p.description_en || "").replace(/<[^>]+>/g, "")}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TODAY'S GREETING */}
      {settings.show_home_greeting && greeting && (
        <section className="bg-cream py-20" data-testid="home-greeting-section">
          <div className="container-ssf grid items-center gap-10 lg:grid-cols-2">
            <div>
              <SectionLabel><Sparkles size={12} className="mr-1 inline" />{t("todays_greeting")}</SectionLabel>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">{pickLocalized(greeting.title)}</h2>
              <p className="mt-4 max-w-lg text-base leading-relaxed text-slate-600">
                {lang === "hi" ? pickLocalized(greeting.message_hi, "hi") || pickLocalized(greeting.message, "hi") : pickLocalized(greeting.message, "en")}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <LinkBtn to={`/greetings/${greeting.slug}`} testid="home-greeting-view">{t("view_greeting")}</LinkBtn>
                <ShareButtons title={pickLocalized(greeting.title)} text={lang === "hi" ? pickLocalized(greeting.message_hi, "hi") || pickLocalized(greeting.message, "hi") : pickLocalized(greeting.message, "en")} url={`${window.location.origin}/greetings/${greeting.slug}`} />
              </div>
            </div>
            {greeting.image && (
              <Link to={`/greetings/${greeting.slug}`} className="card-ssf block overflow-hidden rounded-3xl transition-transform duration-200 hover:-translate-y-1">
                <LazyImg src={greeting.image} alt={pickLocalized(greeting.title)} className="aspect-square w-full object-cover" />
              </Link>
            )}
          </div>
        </section>
      )}

      {/* IN THE NEWS + UPCOMING EVENTS (ATS-style) */}
      {(articles.length > 0 || news.length > 0) && (
        <section className="bg-white py-20 sm:py-24">
          <div className="container-ssf grid gap-14 lg:grid-cols-2">
            <div>
              <SectionLabel>{t("latest_articles")}</SectionLabel>
              <div className="mt-2 flex items-end justify-between">
                <h2 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">{lang === "hi" ? "समाचारों में" : "In the News"}</h2>
                <Link to="/articles" className="text-sm font-bold text-saffron-600 hover:text-navy" data-testid="articles-view-all">{t("view_all")}</Link>
              </div>
              <div className="mt-8 divide-y divide-gray-200 border-y border-gray-200">
                {articles.map((a) => (
                  <article key={a.id} className="group py-6" data-testid={`home-article-${a.slug}`}>
                    <p className="text-xs font-bold uppercase tracking-wider text-saffron-600">{a.category}</p>
                    <h3 className="mt-1.5 font-display text-lg font-bold leading-snug text-navy transition-colors group-hover:text-saffron-600">
                      {lang === "hi" && a.title_hi ? a.title_hi : a.title}
                    </h3>
                    {a.excerpt && <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-slate-600">{a.excerpt}</p>}
                    <Link to={`/articles/${a.slug}`} className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-navy underline decoration-saffron-500 decoration-2 underline-offset-4 hover:text-saffron-600">
                      {lang === "hi" ? "और जानें" : "Learn More"} <ArrowRight size={12} />
                    </Link>
                  </article>
                ))}
                {articles.length === 0 && <p className="py-8 text-sm text-slate-400">Articles will appear here once published.</p>}
              </div>
            </div>
            <div>
              <SectionLabel>{t("latest_news")}</SectionLabel>
              <div className="mt-2 flex items-end justify-between">
                <h2 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">{lang === "hi" ? "आगामी कार्यक्रम" : "Upcoming Events"}</h2>
                <Link to="/news" className="text-sm font-bold text-saffron-600 hover:text-navy" data-testid="news-view-all">{t("view_all")}</Link>
              </div>
              <div className="mt-8 space-y-5">
                {news.map((n) => (
                  <Link key={n.id} to="/news" className="card-ssf group flex gap-5 p-5 transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-lg" data-testid={`home-news-${n.id}`}>
                    <div className="flex h-20 w-20 shrink-0 flex-col items-center justify-center rounded-xl bg-navy text-white">
                      <span className="font-display text-2xl font-black leading-none">{(n.date || "").slice(8, 10) || "--"}</span>
                      <span className="mt-1 text-[10px] font-bold uppercase tracking-wider text-saffron-400">
                        {n.date ? `${new Date(n.date).toLocaleDateString("en", { month: "short", timeZone: "UTC" })} ${n.date.slice(0, 4)}` : ""}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold leading-snug text-navy group-hover:text-saffron-600">{lang === "hi" && n.title_hi ? n.title_hi : n.title}</h3>
                      {(n.venue || n.time) && <p className="mt-1 text-xs text-slate-500">{[n.venue, n.time].filter(Boolean).join(" • ")}</p>}
                      <span className="mt-2 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-saffron-600">
                        {lang === "hi" ? "और जानें" : "Learn More"} <ArrowRight size={12} />
                      </span>
                    </div>
                  </Link>
                ))}
                {news.length === 0 && <p className="py-8 text-sm text-slate-400">News and events will appear here once published.</p>}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* GALLERY PREVIEW */}
      {gallery.length > 0 && (
        <section className="bg-white py-20">
          <div className="container-ssf">
            <div className="flex items-end justify-between">
              <h2 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">{t("from_gallery")}</h2>
              <Link to="/gallery" className="text-sm font-bold text-saffron-600 hover:text-navy" data-testid="gallery-view-all">{t("view_all")}</Link>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
              {gallery.slice(0, 4).map((g) => (
                <Link to="/gallery" key={g.id} className="group overflow-hidden rounded-xl" data-testid={`home-gallery-${g.id}`}>
                  <LazyImg src={g.thumb || g.image} alt={g.heading || g.caption || "Gallery image"} className="aspect-square w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      <section className="py-20 sm:py-24">
        <div className="container-ssf">
          <SectionLabel>{t("testimonials")}</SectionLabel>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {TESTIMONIALS.map((tm, i) => (
              <figure key={i} className="card-ssf flex flex-col p-8" data-testid={`testimonial-${i}`}>
                <blockquote className="flex-1 text-sm leading-relaxed text-slate-600">“{tm.text}”</blockquote>
                <figcaption className="mt-6 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-saffron-100 font-display font-bold text-saffron-600">{tm.name[0]}</span>
                  <span>
                    <span className="block text-sm font-bold text-navy">{tm.name}</span>
                    <span className="block text-xs text-slate-500">{tm.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="grain bg-navy py-20 sm:py-24">
        <div className="container-ssf max-w-3xl">
          <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">{t("cta_title")}</h2>
          <p className="mt-4 text-base leading-relaxed text-slate-300">{t("cta_sub")}</p>
          <div className="mt-8">
            <LinkBtn to="/donate" testid="cta-donate-btn">{t("support_mission")} <ArrowRight size={16} /></LinkBtn>
          </div>
        </div>
      </section>
    </div>
  );
}
